/**
 * What this browser remembers about the person's choices — and only choices. Nothing here is a
 * word anyone said: onboarding seen, analytics consent, whether a released hold sends.
 *
 * Every read and write is guarded. `localStorage` can throw (private windows, site data blocked,
 * a thumbnail capture) and a preference that cannot be stored is an ordinary case, not an outage:
 * the default applies and the page renders.
 */
import { isLanguageChoice, type LanguageChoice } from './i18n.js';

export type AnalyticsConsent = 'granted' | 'denied';

const KEYS = Object.freeze({
  onboarding: 'bilal-onboarding',
  analytics: 'bilal-analytics',
  voiceAutoSend: 'bilal-voice-auto-send',
  firstMessage: 'bilal-first-message',
  language: 'bilal-language',
  linkRequested: 'bilal-link-requested',
});

/** A little longer than a sign-in link lives, so a link opened at its last minute still finishes. */
const LINK_REQUEST_MEMORY_MS = 20 * 60 * 1000;

function read(key: string): string | null {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.localStorage.removeItem(key);
    else globalThis.localStorage.setItem(key, value);
  } catch {
    // A preference that cannot be kept is forgotten, never fatal.
  }
}

export const preferences = Object.freeze({
  /**
   * That this browser asked for a sign-in link a moment ago (ADR-041). A timestamp and nothing
   * else — not the address, never the token. It decides one thing: a link opened in the browser
   * that asked for it finishes by itself, and anywhere else it waits for one press, so a mail
   * scanner following the link spends nothing.
   */
  markLinkRequested: (): void => {
    write(KEYS.linkRequested, String(Date.now()));
  },
  clearLinkRequested: (): void => {
    write(KEYS.linkRequested, null);
  },
  linkRequestedRecently: (): boolean => {
    const at = Number(read(KEYS.linkRequested));
    return Number.isFinite(at) && at > 0 && Date.now() - at < LINK_REQUEST_MEMORY_MS;
  },
  /** True once the person has been through the introduction (or skipped it) on this browser. */
  onboardingDone: (): boolean => read(KEYS.onboarding) === 'done',
  markOnboardingDone: (): void => {
    write(KEYS.onboarding, 'done');
  },
  /** Null until the person has answered; nothing is measured while it is null. */
  analyticsConsent: (): AnalyticsConsent | null => {
    const stored = read(KEYS.analytics);
    return stored === 'granted' || stored === 'denied' ? stored : null;
  },
  /**
   * Whether measurement runs. On unless the person turned it off (founder, 2026-09-14).
   *
   * The stored value stays three-state deliberately — granted, denied, or nothing chosen — because
   * the settings switch must show what it is doing and the notice must be shown once and then not
   * again. What changed is only how "nothing chosen" resolves: it used to mean off and now means
   * on, and `/privacy` was changed in the same commit so the published wording stays true.
   */
  analyticsOn: (): boolean => read(KEYS.analytics) !== 'denied',
  setAnalyticsConsent: (consent: AnalyticsConsent): void => {
    write(KEYS.analytics, consent);
  },
  /** Whether a released hold sends the words (when the transcription is sure). On by default. */
  voiceAutoSend: (): boolean => read(KEYS.voiceAutoSend) !== 'off',
  setVoiceAutoSend: (on: boolean): void => {
    write(KEYS.voiceAutoSend, on ? null : 'off');
  },
  /**
   * The interface language, or `auto` to follow the browser — which is the default, and the reason
   * someone whose phone is in English meets an English product without going looking for a setting.
   */
  language: (): LanguageChoice => {
    const stored = read(KEYS.language);
    return isLanguageChoice(stored) ? stored : 'auto';
  },
  setLanguage: (choice: LanguageChoice): void => {
    write(KEYS.language, choice === 'auto' ? null : choice);
  },
  /** The campaign conversion is the first message ever sent from this browser; it fires once. */
  firstMessageRecorded: (): boolean => read(KEYS.firstMessage) === 'sent',
  markFirstMessage: (): void => {
    write(KEYS.firstMessage, 'sent');
  },
});
